import { z } from "zod";
import { AgentBodySchema } from "./agent.schema";
import { UsageRequestSchema } from "./usage_requests.schema";

const PulseMessageSystemMessageParam = z.object({
  content: z.string(),
  role: z.literal("system"),
  name: z.string().optional(),
});

const PulseMessageUserMessageParam = z.object({
  content: z.union([z.string(), z.array(z.any())]), // Assuming `any` for the content parts for simplicity
  role: z.literal("user"),
  name: z.string().optional(),
});

const PulseMessageAssistantMessageParam = z.object({
  role: z.literal("assistant"),
  content: z.string().nullable().optional(),
  tool_calls: z
    .array(
      z.object({
        type: z.string(),
        function: z.object({
          name: z.string(),
          arguments: z.string().optional(),
        }),
        id: z.string().optional(),
      })
    )
    .optional(), // Assuming `any` for the tool calls for simplicity
});

const PulseMessageToolMessageParam = z.object({
  content: z.string(),
  role: z.literal("tool"),
  tool_call_id: z.string(),
  // AI-1179: result status persisted by the server (success/error).
  status: z.enum(["success", "error"]).optional(),
});

const PulseMessageFunctionMessageParam = z.object({
  content: z.string().nullable(),
  name: z.string(),
  role: z.literal("function"),
});

// AI-1179: structured tool failure persisted by the server next to
// code_execution. `code` is a server catalog key (errors:<code>).
export const ToolErrorDetailsSchema = z.object({
  connection_id: z.string().optional(),
  connection_name: z.string().optional(),
  server_url_host: z.string().optional(),
});

export const ToolErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: ToolErrorDetailsSchema.optional(),
});

export type ToolErrorInfo = z.infer<typeof ToolErrorSchema>;

export const PulseMessageMetadata = z.object({
  usage: z
    .object({
      input_tokens: z.number(),
      output_tokens: z.number(),
      input_tokens_details: z
        .object({
          cached_tokens: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
  execution_time: z.number().optional(),
  // Metricas honestas de velocidade, em ms, vindas do server. Antigas
  // threads e runs sem streaming nao as tem.
  time_to_first_token: z.number().optional(),
  generation_time: z.number().optional(),
  // PROTO-1: model that produced this assistant message (the thread snapshot's
  // model at that turn). Drives the "model changed" divider in the conversation.
  model: z.string().optional(),
  self_managed: z.boolean().optional(),
  cost: z.number().optional(),
  created_at: z.string().optional(),
  status: z.enum(["completed", "running", "pending"]).optional(),
  id: z.string().optional(),
  input_method: z.enum(["text", "voice"]).optional(),
  _voice_ephemeral: z.boolean().optional(),
  _hidden: z.boolean().optional(),
  // Carimbado pelo client no tool message de resposta ao escalate_to_human
  // (POST /threads/{id}/stream), e ecoado de volta pelo server no history.
  // Usado pra distinguir resposta real de "seguiu sem resposta" na UI.
  escalation: z.object({ status: z.enum(["answered", "skipped"]) }).optional(),
  human: z
    .object({
      operator_user_id: z.string(),
      operator_name: z.string(),
      takeover_session_id: z.string(),
    })
    .optional(),
  user_created: z.boolean().optional(),
  // Display-only copy of the model reasoning. Written by the server for verbose
  // threads and never sent back to the model. `status` is client-only transient
  // state (the server never persists it): it lives in the same object so the
  // stream handler does not need a parallel map keyed by message.
  reasoning: z
    .object({
      content: z.string(),
      tokens: z.number().optional(),
      duration_ms: z.number(),
      status: z.enum(["thinking", "streaming", "done"]).optional(),
    })
    .optional(),
  // AI-1179: structured tool failure (see ToolErrorSchema above).
  error: ToolErrorSchema.optional(),
});

export const PulseMessageSchema = z
  .union([
    PulseMessageSystemMessageParam,
    PulseMessageUserMessageParam,
    PulseMessageAssistantMessageParam,
    PulseMessageToolMessageParam,
    PulseMessageFunctionMessageParam,
  ])
  .and(
    z.object({
      metadata: PulseMessageMetadata.optional(),
    })
  );

export const threadTriggers = ["ui", "api", "whatsapp", "sip", "webrtc", "chat-widget", "telegram"] as const;

export const ThreadHumanControlSchema = z.object({
  mode: z.enum(["ai_active", "takeover_pending", "human_active", "release_pending", "closed"]),
  version: z.number(),
  operator_user_id: z.string().optional(),
  operator_name: z.string().optional(),
  takeover_session_id: z.string().optional(),
  taken_over_at: z.string().optional(),
  released_at: z.string().optional(),
});

export const ThreadHumanCapabilitiesSchema = z.object({
  takeover: z.boolean(),
  human_message: z.boolean(),
  release: z.boolean(),
});

export const ChannelMessageSchema = z.object({
  id: z.string(),
  thread_id: z.string(),
  organization_id: z.string(),
  agent_id: z.string(),
  sequence: z.number(),
  channel: z.string(),
  address: z.record(z.string(), z.unknown()),
  direction: z.enum(["inbound", "outbound"]),
  role: z.enum(["user", "assistant"]),
  actor_type: z.enum(["customer", "ai", "human", "system"]),
  actor_user_id: z.string().nullish(),
  actor_name: z.string().nullish(),
  external_message_id: z.string().nullish(),
  idempotency_key: z.string(),
  content: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  delivery_status: z.enum(["queued", "sending", "sent", "failed"]).nullish(),
  delivery_attempt: z.number(),
  provider_message_id: z.string().nullish(),
  last_error: z.string().nullish(),
  sent_at: z.string().nullish(),
  failed_at: z.string().nullish(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export const ThreadSchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  user_id: z.string(),
  agent_id: z.string(),
  history: z.array(PulseMessageSchema).optional(),
  pending_human_messages: z
    .array(z.object({ content: z.string(), created_at: z.string().optional() }))
    .optional(),
  human_control: ThreadHumanControlSchema.optional(),
  human_capabilities: ThreadHumanCapabilitiesSchema.optional(),
  channel_messages: z.array(ChannelMessageSchema).optional(),
  title: z.string().nullish(),
  additional_instructions: z.string().nullish(),
  vars: z.record(z.string(), z.any()).optional(),
  agent_snapshot: AgentBodySchema.nullish(),
  status: z.enum(["pending", "closed", "active", "running"]).default("active"),
  triggered: z.enum(threadTriggers).default("api"),
  identifier: z.string().nullish(),
  deleted_at: z.string().nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  is_shared: z.boolean().default(false),
  share_token: z.string().nullish(),
  shared_at: z.string().nullish(),
  post_analysis: z
    .array(
      z.object({
        name: z.string(),
        description: z.string(),
        content: z.record(z.string(), z.any()),
      })
    )
    .nullish(),
});

export const ThreadWithUsageRequestSchema = ThreadSchema.extend({
  usage_requests: z.array(UsageRequestSchema).nullish(),
  active_session_thread_id: z.string().optional(),
});

export type PulseMessageSystemMessage = z.infer<typeof PulseMessageSystemMessageParam>;
export type PulseMessageUserMessage = z.infer<typeof PulseMessageUserMessageParam>;
export type PulseMessageAssistantMessage = z.infer<typeof PulseMessageAssistantMessageParam>;
export type PulseToolMessage = z.infer<typeof PulseMessageToolMessageParam>;
export type PulseMessageFunctionMessage = z.infer<typeof PulseMessageFunctionMessageParam>;
export type PulseMessage = z.infer<typeof PulseMessageSchema>;
export type ThreadHumanControl = z.infer<typeof ThreadHumanControlSchema>;
export type ThreadHumanCapabilities = z.infer<typeof ThreadHumanCapabilitiesSchema>;
export type ChannelMessage = z.infer<typeof ChannelMessageSchema>;
export type Thread = z.infer<typeof ThreadSchema>;
export type ThreadWithUsageRequest = z.infer<typeof ThreadWithUsageRequestSchema>;
